import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStudentReview } from '../../api/resultsApi';
import { FaArrowLeft, FaCheckCircle, FaTimesCircle, FaMinusCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const StudentResultPage = () => {
  const { driveId, attemptId } = useParams();
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    const fetchReview = async () => {
      setLoading(true);
      setReviewData(null);
      setCurrentQuestionIndex(0);
      try {
        const res = await getStudentReview(driveId, attemptId);
        setReviewData(res.data);
      } catch (err) {
        console.error('Failed to fetch student review', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [driveId, attemptId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary-custom"></div>
      </div>
    );
  }

  if (!reviewData) {
    return (
      <div className="p-4 text-center text-muted">
        <h4>Review Data Not Found</h4>
        <Link to={`/admin/drives/${driveId}/results`} className="btn btn-outline-primary mt-3">Back to Results</Link>
      </div>
    );
  }

  const { studentDetails, assessmentSummary, questionReview, codingReview = [], summary, difficultySummary, violationsSummary, violations } = reviewData;
  const currentQuestion = questionReview[currentQuestionIndex];

  return (
    <div className="container-fluid p-0">
      <div className="mb-4 d-flex align-items-center gap-3">
        <Link to={`/admin/drives/${driveId}/results`} className="btn btn-light rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center">
          <FaArrowLeft />
        </Link>
        <div>
          <h3 className="fw-bold mb-1">Student Review</h3>
          <p className="text-muted mb-0">{studentDetails.name} - {studentDetails.drive}</p>
        </div>
      </div>

      <div className="row g-4 mb-4">
        {/* Student Details Card */}
        <div className="col-lg-4">
          <div className="card custom-card border-0 shadow-sm p-4 h-100">
            <h5 className="fw-bold border-bottom pb-2 mb-3">Student Details</h5>
            <div className="d-flex align-items-center mb-3">
              {studentDetails.faceImageUrl ? (
                <img src={`http://localhost:5000/uploads/${studentDetails.faceImageUrl}`} alt="Student Face" className="rounded-circle me-3" style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
              ) : (
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center text-muted border me-3" style={{ width: '60px', height: '60px', fontSize: '0.75rem' }}>No Photo</div>
              )}
              <div>
                <h6 className="fw-bold mb-0">{studentDetails.name}</h6>
                <small className="text-muted">{studentDetails.email}</small>
              </div>
            </div>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between"><span className="text-muted">Roll No</span> <span className="fw-semibold text-end">{studentDetails.rollNumber}</span></div>
              <div className="d-flex justify-content-between"><span className="text-muted">College</span> <span className="fw-semibold text-end">{studentDetails.college}</span></div>
            </div>
          </div>
        </div>

        {/* Assessment Summary Card */}
        <div className="col-lg-4">
          <div className="card custom-card border-0 shadow-sm p-4 h-100">
            <h5 className="fw-bold border-bottom pb-2 mb-3">Assessment Summary</h5>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between"><span className="text-muted">Score</span> <span className="fw-bold text-primary-custom">{assessmentSummary.score}</span></div>
              <div className="d-flex justify-content-between"><span className="text-muted">Percentage</span> <span className="fw-semibold">{assessmentSummary.percentage.toFixed(2)}%</span></div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Status</span> 
                <span className={`badge rounded-pill ${assessmentSummary.passFail === 'Pass' ? 'bg-success' : 'bg-danger'}`}>{assessmentSummary.passFail}</span>
              </div>
              <div className="d-flex justify-content-between"><span className="text-muted">Time Taken</span> <span className="fw-semibold">{assessmentSummary.timeTaken} mins</span></div>
              <div className="d-flex justify-content-between"><span className="text-muted">Submitted</span> <span className="fw-semibold">{new Date(assessmentSummary.submittedTime).toLocaleString()}</span></div>
            </div>
          </div>
        </div>

        {/* Performance Summary Card */}
        <div className="col-lg-4">
          <div className="card custom-card border-0 shadow-sm p-4 h-100 bg-light">
            <h5 className="fw-bold border-bottom pb-2 mb-3">Performance Overview</h5>
            <div className="row text-center g-2 mt-2">
              <div className="col-4">
                <div className="p-3 bg-white rounded shadow-sm border-bottom border-4 border-success">
                  <FaCheckCircle className="text-success fs-3 mb-2" />
                  <h4 className="fw-bold mb-0">{summary.correct}</h4>
                  <small className="text-muted">Correct</small>
                </div>
              </div>
              <div className="col-4">
                <div className="p-3 bg-white rounded shadow-sm border-bottom border-4 border-danger">
                  <FaTimesCircle className="text-danger fs-3 mb-2" />
                  <h4 className="fw-bold mb-0">{summary.wrong}</h4>
                  <small className="text-muted">Wrong</small>
                </div>
              </div>
              <div className="col-4">
                <div className="p-3 bg-white rounded shadow-sm border-bottom border-4 border-secondary">
                  <FaMinusCircle className="text-secondary fs-3 mb-2" />
                  <h4 className="fw-bold mb-0">{summary.skipped}</h4>
                  <small className="text-muted">Skipped</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Difficulty Summary Section */}
      {difficultySummary && (
        <div className="card custom-card border-0 shadow-sm p-4 mb-4 bg-light">
          <h5 className="fw-bold border-bottom pb-2 mb-3">Difficulty Summary</h5>
          <div className="row text-center g-3 mt-1">
            {['easy', 'medium', 'hard'].map((level) => (
              <div className="col-md-4" key={level}>
                <div className={`p-3 bg-white rounded shadow-sm border-bottom border-4 ${level === 'easy' ? 'border-success' : level === 'medium' ? 'border-warning' : 'border-danger'}`}>
                  <h5 className="fw-bold text-capitalize mb-3">{level}</h5>
                  <div className="d-flex justify-content-between px-2">
                    <div className="text-center">
                      <div className="fw-bold fs-5">{difficultySummary[level].attempted}</div>
                      <small className="text-muted">Attempted</small>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold fs-5 text-success">{difficultySummary[level].correct}</div>
                      <small className="text-muted">Correct</small>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold fs-5 text-danger">{difficultySummary[level].wrong}</div>
                      <small className="text-muted">Wrong</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Malfunction Details Section */}
      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <div className="card custom-card border-0 shadow-sm p-4 h-100">
            <h5 className="fw-bold border-bottom pb-2 mb-3 text-warning">
              <FaTimesCircle className="me-2" /> Malfunction Summary
            </h5>
            <div className="row g-3">
              {Object.entries(violationsSummary || {}).map(([key, count]) => (
                <div className="col-6 d-flex justify-content-between border-bottom pb-2" key={key}>
                  <span className="text-muted small">{key.replace(/_/g, ' ')}</span>
                  <span className={`fw-bold ${count > 0 ? 'text-danger' : 'text-success'}`}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card custom-card border-0 shadow-sm p-4 h-100">
            <h5 className="fw-bold border-bottom pb-2 mb-3 text-warning">
              <FaCheckCircle className="me-2 text-warning" /> Violation Timeline
            </h5>
            {(!violations || violations.length === 0) ? (
              <div className="text-center text-muted p-4">No violations detected.</div>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '200px' }}>
                <ul className="list-group list-group-flush">
                  {violations.map((v, idx) => (
                    <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2" key={idx}>
                      <span className="text-muted small">{new Date(v.time).toLocaleTimeString()}</span>
                      <span className="badge bg-danger rounded-pill">{v.type.replace(/_/g, ' ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Question Review Section */}
      {questionReview.length > 0 && (
        <div className="card custom-card border-0 shadow-sm p-0 overflow-hidden mb-4">
          <div className="bg-primary-custom text-white p-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-bold">Question {currentQuestionIndex + 1} of {questionReview.length}</h5>
            <div className="d-flex gap-3 align-items-center">
              <span className={`badge rounded-pill bg-white text-dark px-3 py-2 fw-bold d-flex align-items-center gap-2`}>
                {currentQuestion.status === 'Correct' && <FaCheckCircle className="text-success" />}
                {currentQuestion.status === 'Wrong' && <FaTimesCircle className="text-danger" />}
                {currentQuestion.status === 'Skipped' && <FaMinusCircle className="text-secondary" />}
                {currentQuestion.status}
              </span>
              <span className="badge bg-light text-dark px-3 py-2">Marks: {currentQuestion.marks}</span>
            </div>
          </div>
          
          <div className="p-4 p-md-5 bg-light">
            <h4 className="mb-4 lh-base" dangerouslySetInnerHTML={{ __html: currentQuestion.questionText }}></h4>
            
            <div className="row g-3">
              {currentQuestion.options.map((opt, idx) => {
                let borderClass = "border-secondary-subtle";
                let bgClass = "bg-white";
                let icon = null;
                
                if (opt.id === currentQuestion.correctAnswerId) {
                  borderClass = "border-success";
                  bgClass = "bg-success bg-opacity-10";
                  icon = <FaCheckCircle className="text-success fs-5" />;
                } else if (opt.id === currentQuestion.studentAnswerId && currentQuestion.status === 'Wrong') {
                  borderClass = "border-danger";
                  bgClass = "bg-danger bg-opacity-10";
                  icon = <FaTimesCircle className="text-danger fs-5" />;
                }

                return (
                  <div key={opt.id} className="col-md-6">
                    <div className={`p-3 rounded border border-2 ${borderClass} ${bgClass} d-flex align-items-center justify-content-between`}>
                      <div className="d-flex gap-3 align-items-center">
                        <span className="fw-bold text-muted">{String.fromCharCode(65 + idx)}.</span>
                        <span dangerouslySetInnerHTML={{ __html: opt.text }}></span>
                      </div>
                      {icon && <div>{icon}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {currentQuestion.explanation && currentQuestion.explanation !== 'N/A' && (
              <div className="mt-4 p-3 bg-white rounded border-start border-4 border-info">
                <h6 className="fw-bold text-info mb-2">Explanation</h6>
                <p className="mb-0 text-muted">{currentQuestion.explanation}</p>
              </div>
            )}
          </div>
          
          <div className="p-3 border-top bg-white d-flex justify-content-between align-items-center">
            <button 
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
            >
              <FaChevronLeft /> Previous
            </button>
            
            <div className="d-none d-md-flex gap-1 overflow-auto" style={{ maxWidth: '60%' }}>
              {questionReview.map((q, idx) => (
                <button
                  key={q.questionId}
                  className={`btn btn-sm ${idx === currentQuestionIndex ? 'btn-primary' : 'btn-light'} ${q.status === 'Correct' ? 'border-success' : q.status === 'Wrong' ? 'border-danger' : ''}`}
                  onClick={() => setCurrentQuestionIndex(idx)}
                  style={{ width: '36px' }}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
            
            <button 
              className="btn btn-primary-custom d-flex align-items-center gap-2"
              onClick={() => setCurrentQuestionIndex(prev => Math.min(questionReview.length - 1, prev + 1))}
              disabled={currentQuestionIndex === questionReview.length - 1}
            >
              Next <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Coding Submissions */}
      {codingReview.length > 0 && (
        <div className="card custom-card border-0 shadow-sm p-0 overflow-hidden mb-4">
          <div className="bg-primary-custom text-white p-3">
            <h5 className="mb-0 fw-bold">Coding Submissions ({codingReview.length}) <small className="fw-normal opacity-75">(the student's last submission is graded)</small></h5>
          </div>
          <div className="p-3 p-md-4 bg-light d-flex flex-column gap-4">
            {codingReview.map((c) => (
              <div key={c.questionId} className="bg-white border rounded-3 p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                  <div className="fw-semibold" style={{ whiteSpace: 'pre-line' }}>{c.questionText}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <span className={`badge ${c.status === 'Accepted' ? 'bg-success' : c.status === 'Not submitted' ? 'bg-secondary' : c.passedCount > 0 ? 'bg-warning text-dark' : 'bg-danger'}`}>{c.status}</span>
                    {c.submissions > 0 && <span className="badge bg-light text-dark border">{c.passedCount}/{c.totalCount} test cases</span>}
                    <span className="badge bg-light text-dark border">Marks: {c.marks}/{c.maxMarks}</span>
                    <span className="badge bg-light text-dark border">{c.language}</span>
                    <span className="badge bg-light text-muted border">{c.submissions === 0 ? 'Only ran, never submitted (latest code shown)' : `${c.submissions} submission${c.submissions > 1 ? 's' : ''}`}</span>
                  </div>
                </div>
                <pre className="bg-dark text-light rounded p-3 mb-0 small" style={{ maxHeight: 320, overflow: 'auto' }}>{c.sourceCode}</pre>
                {c.runs && c.runs.length > 0 && (
                  <details className="mt-3">
                    <summary className="small fw-semibold text-muted" style={{ cursor: 'pointer' }}>Latest run ({c.runs[0].runCount || 1} run{(c.runs[0].runCount || 1) > 1 ? 's' : ''} used; only the latest is kept)</summary>
                    <div className="d-flex flex-column gap-2 mt-2">
                      {c.runs.map((r, i) => (
                        <div key={i} className="border rounded p-2 small">
                          <div className="d-flex flex-wrap gap-2 align-items-center mb-1">
                            <span className="text-muted">{new Date(r.at).toLocaleString()}</span>
                            <span className={`badge ${r.verdict === 'Accepted' ? 'bg-success' : 'bg-danger'}`}>{r.verdict}</span>
                            <span className="badge bg-light text-dark border">{r.language}</span>
                            <span className="badge bg-light text-dark border">{r.isCustom ? 'Custom input' : `${r.passedCount}/${r.totalCount} sample tests`}</span>
                          </div>
                          {r.isCustom && <div className="text-muted mb-1">Input: <code>{r.input}</code>{r.output ? <> &rarr; Output: <code>{r.output}</code></> : null}</div>}
                          <pre className="bg-dark text-light rounded p-2 mb-0" style={{ maxHeight: 200, overflow: 'auto' }}>{r.sourceCode}</pre>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentResultPage;
